import React, { useState } from "react";
import { Form, FormGroup, Button } from "reactstrap";
import PhoneInputWithCountrySelect from "react-phone-number-input";
import {
  buildRegistrationConsent,
  EMAIL_PLACEHOLDER,
  USER_ID_PLACEHOLDER,
} from "../utils/registrationValidationStrings";

const HEX_COLOR = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;

const DEFAULT_HEADING_COLOR = "#ff4500";

// WCAG relative luminance of a #rgb / #rrggbb colour.
const relativeLuminance = (hex) => {
  let digits = hex.slice(1);
  if (digits.length === 3) {
    digits = digits
      .split("")
      .map((c) => c + c)
      .join("");
  }
  const channel = (start) => {
    const v = parseInt(digits.substr(start, 2), 16) / 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * channel(0) + 0.7152 * channel(2) + 0.0722 * channel(4);
};

const RegistrationForm = ({
  phoneNumber,
  setPhoneNumber,
  firstName,
  setFirstName,
  lastName,
  setLastName,
  onSubmit,
  companyName,
  logoUrl,
  brandColor,
  instruction,
  showEmail,
  showUserId,
  email,
  setEmail,
  userId,
  setUserId,
  formError,
  isLoading,
}) => {
  const isMobile = window.innerWidth < 768;
  const [logoFailed, setLogoFailed] = useState(false);

  // The brand colour is the only dynamic style; an unusable value leaves the default look.
  const brandStyle = {};
  let brandClass = "";
  if (typeof brandColor === "string" && HEX_COLOR.test(brandColor)) {
    const luminance = relativeLuminance(brandColor);
    brandClass = "bwai-reg-branded";
    brandStyle["--bwai-reg-brand"] = brandColor;
    // Button text: black or white, whichever reads better. Heading: brand colour only when it
    // keeps 3:1 contrast on the white card; a pale brand colour falls back to the default.
    brandStyle["--bwai-reg-brand-text"] = luminance > 0.179 ? "#000000" : "#ffffff";
    brandStyle["--bwai-reg-heading"] = luminance <= 0.3 ? brandColor : DEFAULT_HEADING_COLOR;
  }

  const inputStyle = {
    width: "100%",
    height: isMobile ? "60px" : "50px",
    fontSize: "16px",
  };

  return (
    <div className={brandClass} style={brandStyle}>
      {logoUrl && !logoFailed && (
        <img
          className="bwai-reg-logo"
          src={logoUrl}
          alt={companyName}
          onError={() => setLogoFailed(true)}
        />
      )}
      <h1
        className="mfa-title"
        style={{ fontSize: isMobile ? "24px" : "28px" }}
      >
        Enter your contact information
      </h1>
      <p
        className="mfa-subtitle"
        style={{ fontSize: isMobile ? "14px" : "16px" }}
      >
        Your company requires your contact information for employee
        registration.
      </p>
      {instruction && <p className="bwai-reg-instruction">{instruction}</p>}

      <Form onSubmit={onSubmit} noValidate>
        <FormGroup>
          <input
            type="text"
            className="form-control"
            placeholder="First Name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            style={{
              width: "100%",
              height: isMobile ? "60px" : "50px",
              fontSize: isMobile ? "16px" : "16px",
            }}
          />
        </FormGroup>
        <FormGroup>
          <input
            type="text"
            className="form-control"
            placeholder="Last Name"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            style={{
              width: "100%",
              height: isMobile ? "60px" : "50px",
              fontSize: isMobile ? "16px" : "16px",
            }}
          />
        </FormGroup>
        {showEmail && (
          <FormGroup>
            <input
              type="email"
              className="form-control"
              placeholder={EMAIL_PLACEHOLDER}
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={inputStyle}
            />
          </FormGroup>
        )}
        {showUserId && (
          <FormGroup>
            <input
              type="text"
              className="form-control"
              placeholder={USER_ID_PLACEHOLDER}
              autoComplete="off"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              style={inputStyle}
            />
          </FormGroup>
        )}
        <FormGroup>
          <div className="phone-input-row">
            <PhoneInputWithCountrySelect
              placeholder="Enter Mobile number"
              value={phoneNumber}
              onChange={setPhoneNumber}
              className="form-control p-0"
              defaultCountry="US"
              style={{
                width: "100%",
                height: isMobile ? "60px" : "50px",
                fontSize: isMobile ? "16px" : "16px",
              }}
            />
          </div>
          <p className="bwai-reg-consent">{buildRegistrationConsent(companyName)}</p>
        </FormGroup>

        {formError && (
          <p className="bwai-reg-error" role="alert">
            {formError}
          </p>
        )}

        <Button
          type="submit"
          color="dark"
          className="continue-btn"
          block
          disabled={isLoading}
          style={{ height: isMobile ? "45px" : "50px" }}
        >
          Submit
        </Button>
      </Form>
    </div>
  );
};

export default RegistrationForm;
